import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Users, Search, Plus, X, MessageSquare, Crown, Flame,
} from "lucide-react";
import { GenreFilter } from "@/components/GenrePicker";
import { GENRE_FAMILIES, ALL_GENRES } from "@/config/genres";
import { toast } from "sonner";

export default function GroupsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("discover");
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState("Tous");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState("popular");
  const [newGroup, setNewGroup] = useState({ name: "", description: "", genre: "" });
  const [createLoading, setCreateLoading] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const params = { limit: 30 };
      if (search) params.search = search;
      if (genre !== "Tous") params.genre = genre;
      params.sort = sort;
      const res = await api.get("/groups", { params });
      setGroups(res.data.groups);
    } catch (err) { console.error(err); }
  }, [api, search, genre, sort]);

  const fetchMyGroups = useCallback(async () => {
    try {
      const res = await api.get("/groups/my");
      setMyGroups(res.data.groups);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchGroups(), fetchMyGroups()]).finally(() => setLoading(false));
  }, [fetchGroups, fetchMyGroups]);

  const handleJoin = async (groupId) => {
    try {
      const res = await api.post(`/groups/${groupId}/join`);
      const update = (list) =>
        list.map((g) =>
          g.id === groupId
            ? { ...g, is_member: res.data.joined, member_count: res.data.member_count }
            : g
        );
      setGroups(update);
      fetchMyGroups();
    } catch (err) { console.error(err); }
  };

  const handleCreate = async () => {
    if (!newGroup.name.trim()) return;
    setCreateLoading(true);
    try {
      const res = await api.post("/groups", {
        name: newGroup.name,
        description: newGroup.description,
        genre: newGroup.genre || null,
      });
      setCreating(false);
      setNewGroup({ name: "", description: "", genre: "" });
      navigate(`/groups/${res.data.group.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setCreateLoading(false);
  };

  const displayedGroups = tab === "my" ? myGroups : groups;

  return (
    <div className="py-6 space-y-6" data-testid="groups-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground mb-1">Communautes</h1>
          <p className="text-sm text-muted-foreground">Rejoins des groupes de fans</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="bg-[#FF6B35] text-white font-bold px-4 py-2 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform flex items-center gap-1.5"
          data-testid="create-group-btn"
        >
          <Plus className="w-4 h-4" />
          Creer
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-full p-1 border border-border">
        <button
          onClick={() => setTab("discover")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "discover" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="tab-discover-groups"
        >
          <Search className="w-4 h-4" />
          Decouvrir
        </button>
        <button
          onClick={() => setTab("my")}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            tab === "my" ? "bg-[#FF6B35] text-white" : "text-muted-foreground"
          }`}
          data-testid="tab-my-groups"
        >
          <Users className="w-4 h-4" />
          Mes groupes ({myGroups.length})
        </button>
      </div>

      {/* Search (discover only) */}
      {tab === "discover" && (
        <>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un groupe..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-card border-border rounded-full text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] h-11"
              data-testid="groups-search"
            />
          </div>
          <GenreFilter selected={genre} onChange={setGenre} />

          {/* Sort buttons */}
          <div className="flex gap-2">
            {[
              { key: "popular", label: "Populaires" },
              { key: "active", label: "En feu" },
              { key: "recent", label: "Recents" },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setSort(s.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                  sort === s.key
                    ? "bg-[#7000FF]/10 border-[#7000FF]/40 text-[#7000FF]"
                    : "bg-card border-border text-muted-foreground"
                }`}
              >
                {s.key === "active" && <Flame className="w-3 h-3 inline mr-1" />}
                {s.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Groups list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-foreground/5 rounded-3xl h-24 animate-pulse" />
          ))}
        </div>
      ) : displayedGroups.length === 0 ? (
        <div className="text-center py-16">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            {tab === "my" ? "Tu n'as rejoint aucun groupe" : "Aucun groupe trouve"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {tab === "my" ? "Explore et rejoins des communautes !" : "Sois le premier a en creer un !"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedGroups.map((g) => (
            <div
              key={g.id}
              className="bg-card border border-border rounded-3xl p-5 hover:border-[#FF6B35]/30 transition-colors duration-300 cursor-pointer group shadow-sm"
              onClick={() => navigate(`/groups/${g.id}`)}
              data-testid={`group-card-${g.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#7000FF] to-[#FF8FA3] flex items-center justify-center shrink-0 group-hover:shadow-lg group-hover:shadow-[#7000FF]/20 transition-shadow relative">
                  <Users className="w-6 h-6 text-white" />
                  {g.is_hot && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B35] rounded-full flex items-center justify-center shadow-lg" data-testid={`hot-badge-${g.id}`}>
                      <Flame className="w-3 h-3 text-white" />
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-bold text-card-foreground truncate group-hover:text-[#FF6B35] transition-colors">
                    {g.name}
                  </h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Users className="w-3 h-3" />
                      {g.member_count} membre{g.member_count > 1 ? "s" : ""}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <MessageSquare className="w-3 h-3" />
                      {g.post_count || 0} post{(g.post_count || 0) > 1 ? "s" : ""}
                    </span>
                    {g.genre && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-card-foreground/80 border border-border">
                        {g.genre}
                      </span>
                    )}
                  </div>
                  {g.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">{g.description}</p>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJoin(g.id);
                  }}
                  className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-all duration-200 shrink-0 ${
                    g.is_member
                      ? "bg-muted text-muted-foreground border border-border hover:bg-border"
                      : "bg-[#FF6B35] text-white shadow-[0_0_15px_rgba(255,107,53,0.3)] hover:scale-105 active:scale-95"
                  }`}
                  data-testid={`join-btn-${g.id}`}
                >
                  {g.is_member ? (g.user_role === "admin" ? "Admin" : "Membre") : "Rejoindre"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create group modal */}
      {creating && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="create-group-modal">
          <div className="bg-card border border-border rounded-3xl p-6 w-full max-w-md space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg text-card-foreground">Nouveau groupe</h3>
              <button onClick={() => setCreating(false)} className="text-muted-foreground hover:text-card-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <Input
              placeholder="Nom du groupe"
              value={newGroup.name}
              onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
              className="bg-white border-border rounded-xl text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] h-11"
              data-testid="group-name-input"
            />
            <textarea
              placeholder="Description (optionnel)"
              value={newGroup.description}
              onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
              className="w-full bg-white border border-border rounded-xl px-4 py-3 text-sm text-card-foreground placeholder:text-muted-foreground focus:border-[#FF6B35] focus:ring-1 focus:ring-[#FF6B35] outline-none resize-none h-20"
              data-testid="group-description-input"
            />
            <div>
              <p className="text-xs text-muted-foreground mb-2">Genre (optionnel)</p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_GENRES.slice(0, 30).map((g) => (
                  <button
                    key={g}
                    onClick={() => setNewGroup({ ...newGroup, genre: newGroup.genre === g ? "" : g })}
                    className={`text-[10px] px-2.5 py-1 rounded-full border transition-all ${
                      newGroup.genre === g
                        ? "bg-[#FF6B35]/10 border-[#FF6B35]/40 text-[#FF6B35]"
                        : "bg-white border-border text-muted-foreground"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={handleCreate}
              disabled={createLoading || !newGroup.name.trim()}
              className="w-full bg-[#FF6B35] text-white font-bold py-3 rounded-full text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform disabled:opacity-50"
              data-testid="group-create-submit"
            >
              {createLoading ? "..." : "Creer le groupe"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
